import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { MatSpinner } from '@angular/material/progress-spinner';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Feature } from 'geojson';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Tempo } from 'src/app/instant-analysis/models/tempo';
import { MapPainterService } from 'src/app/instant-analysis/services/map-painter/map-painter.service';
import { MathAux } from 'src/app/instant-analysis/services/math-aux/math-aux';
import { ServiceService } from 'src/app/instant-analysis/services/service.service';
import { Point } from '@turf/helpers';
import { TimespanService } from 'src/app/instant-analysis/services/timespan/timespan.service';
import { CSVAux } from 'src/app/instant-analysis/services/csv-aux/csv-aux';
import { TimeFormatter } from 'src/app/instant-analysis/utils/time-formatter';

/**
 * Heatmatrix específica da página da análise intervalar.
 * Em vez de ter as colunas dos acumulados de chuva, tem as seguintes colunas:
 * duração total da chuva, duração das intensidades fraca, moderada, forte e muito forte,
 * porcentagem de registros que apresentaram chuva, média, moda e mediana dentre
 * os índices pluviométricos maiores que zero.
 */
@Component({
  selector: 'app-history-heatmatrix',
  templateUrl: './history-heatmatrix.component.html',
  styleUrls: ['./history-heatmatrix.component.css']
})
export class HistoryHeatmatrixComponent implements OnInit {
  displayedArray: Feature<GeoJSON.Point>[] = [];

  stationArray: Feature<GeoJSON.Point>[] = [];
  neighborhoodArray: Feature<GeoJSON.Point>[] = [];
  activeRegions: Feature<Point>[][] = [];

  displayState = 1;
  displayedColumns: string [] = [];

  durationColumnNames = [
    'total', 
    'nfraca',
    'nmoderada',
    'nforte',
    'nmuitoforte'
  ];

  timeColumns: {name: string, text: string,color:string,selected:boolean}[] = [
    { name:'total', text: 'Total de chuva',color:'indice_pluvcolor',selected: true},
    { name:'percent', text: '% de chuva',color:'indice_pluvcolor',selected: false},
    { name:'nfraca', text: 'Total de fraca',color:'indice_pluvcolor',selected: false},
    { name:'nmoderada', text: 'Total de moderada',color:'quinzemincolor',selected: false},
    { name:'nforte', text: 'Total de forte',color:'trintamincolor',selected: false},
    { name:'nmuitoforte', text: 'Total de muito forte',color:'umahoracolor',selected: false},
    { name:'mean', text: 'Média',color:'quinzemincolor',selected: false},
    { name:'mode', text: 'Moda',color:'trintamincolor',selected: false},
    { name:'median', text: 'Mediana',color:'umahoracolor',selected: false}
  ];

  dataSource = new MatTableDataSource(this.displayedArray);
  clickedRows = new Set<Feature<GeoJSON.Point>>();
  sortMode = 1
  heatmatrixClass = "heatmatrix-hidden"
  button_visibility = "heatmatrix-ctrl-button visible"
  selectedId = -1
  selectedNeighborhood = ""
  displayName = "Estações"
  lastDateListSearched = -1
  returnTempoId = -1
  actualTempoId = -1
  backdrop = this.createBackdrop();
  Tempos$ : Observable<any[]>
  datelist_show = false;
  selectedStations: string[] = [];
  tempo:Tempo[];


  @Output() timespanClicked = new EventEmitter<string>();
  @Output() rowClicked = new EventEmitter<Feature<GeoJSON.Point>>();
  @Output() tableClosed = new EventEmitter<boolean>();
  @Output() dateListFound = new EventEmitter<[string,Tempo[]]>(); 
  @Output() searchDate = new EventEmitter<Tempo>();
  @Output() stationToggled = new EventEmitter<string>();
  @Output() changeTableDisplay = new EventEmitter<Feature<Point>[][]>();
  @Output() dragDisabled = new EventEmitter<boolean>();

  @ViewChild(MatTable) table: MatTable<any>;

  @ViewChild(MatSort) sort: MatSort;
    ngAfterViewInit() {
      this.dataSource.sort = this.sort;
    }

  constructor(private painter : MapPainterService, private service: ServiceService,private overlay: Overlay, private timespanService:TimespanService) {
    // inicializa o array dos nomes de referência das colunas do componente tabela.
    this.displayedColumns = ['station'].concat(this.getTimeColumnsNames());
  } 

  ngOnInit(): void {
  }

  /**
   * Alterna a exibição da heatmatrix.
   * @param toggle indica se a heatmatrix será exibida ou escondida.
   */
  changeView(toggle: boolean){

    this.heatmatrixClass = toggle ? 'heatmatrix-show' : 'heatmatrix-hidden';

    if (toggle == false) {
      this.tableClosed.emit();
    }
  }

  /**
   * Reinicia a heatmatrix.
   * @param newSearch indica se é uma consulta em um intervalo de tempo diferente.
   * @param tempo intervalo do tempo.
   * @param selectedTimestamp nome da coluna selecionada.
   */
  reset(newSearch: boolean, tempo: Tempo[], selectedTimestamp: string) {

    this.actualTempoId = tempo[0].id_tempo;
    this.activateColumn(selectedTimestamp);
    this.tempo = tempo;
    this.activeRegions = [];
  }

  /**
   * Marca uma coluna da heatmatrix como ativa.
   * @param selectedTimestamp nome da coluna a ser ativada.
   */
  activateColumn(selectedTimestamp: string) {

    // Primeiro, desabilita todas as colunas
    this.timeColumns.forEach( (val)=>{
      val.selected = false
    });

    // Depois, marca a coluna selecionada
    const filtered = this.timeColumns.filter(val => val.name == selectedTimestamp);
    if (filtered == null || filtered.length == 0) {
      return;
    }
    filtered[0].selected = true;
  }

  /**
   * Atualiza a heatmatrix com novos dados das estações e dos bairros.
   * @param stations pontos das estações com as estatísticas calculadas.
   * @param neighborhoods centroides dos bairros com as estatísticas calculadas.
   */
  updateTable(stations: Feature<GeoJSON.Point>[], neighborhoods:Feature<GeoJSON.Point>[]){

    this.stationArray = stations;
    this.neighborhoodArray = this.removeDuplicateNeighborhoodNames(neighborhoods);
    if (this.displayState == 1) this.displayedArray = this.stationArray;
    else this.displayedArray = this.neighborhoodArray;
    this.table.renderRows()
  }

  /**
   * Trata o click em uma linha, destacando a estação ou bairro correspondente.
   * @param row entrada da tabela clicada.
   */
  onRowClicked(row:Feature<GeoJSON.Point>){

    const { neighborhood, region } = row.properties;

    if (region) {
      return;
    }

    if (neighborhood) {
      this.onNeighborhoodRowClicked(row);
      return;
    }

    this.onStationRowClicked(row);    
  }

  /**
   * Trata o click em uma linha de bairro na tabela.
   * @param row linha de estação clicada.
   */
  onNeighborhoodRowClicked(row: Feature<Point>) {

    const { nome_estacao_original } = row.properties;

    if (this.selectedNeighborhood != nome_estacao_original) {
      this.selectedNeighborhood = nome_estacao_original;
      this.rowClicked.emit(row);
      return;
    }

    this.selectedNeighborhood = "";
    this.rowClicked.emit({ 
      "type": "Feature", 
      "properties": {"neighborhood": true, "erase": true}, 
      "geometry": {"type":"Point","coordinates":[]} 
    });
  }

  /**
   * Trata o click em uma linha de estação na tabela.
   * @param row linha de bairro clicada.
   */
  onStationRowClicked(row: Feature<Point>) {

    const { id } = row.properties;

    if (this.selectedId != id) {
      this.selectedId = id;
      this.rowClicked.emit(row);
      return;
    }

    this.selectedId = -1;
    this.rowClicked.emit({ 
      "type": "Feature", 
      "properties": {"erase":true}, 
      "geometry": {"type":"Point","coordinates":[]} 
    });    
  }

  /**
   * Trata o click em uma coluna da tabela, emitindo evento para atualizar o mapa
   * usando a coluna selecionada.
   * @param timespan nome da coluna selecionada.
   */
  onTimespanClicked(timespan:string){
    this.timeColumns.forEach( (val) =>{
      if (val.name == timespan)
        val.selected = true;
      else
        val.selected = false
    })
    this.timespanClicked.emit(timespan)
  }

  /**
   * Obtém um array com os nomes das colunas dos tempos.
   * Usado para inicializar o array displayedColumns, que será usado no componente tabela do Material.
   * @returns nomes das colunas, usados como referências no componente tabela.
   */
  getTimeColumnsNames(): string[] {
    return this.timeColumns.map(s => s.name);
  }

  /**
   * Chama método auxiliar para arredondar o número de casas decimais de um número
   * 
   * @param val número decimal
   * @param decimalPlaces número de casas decimais que serão mantidas
   * @returns número com <decimalPlaces> casas decimais
   */
  round(val: number, decimalPlaces: number): number {
    return MathAux.round(val, decimalPlaces);
  }

  /**
   * Retorna o estado de discreção de uma estação em um determinado intervalo de tempo
   * 
   * @param station estação 
   * @param timestamp intervalo de tempo que deseja 
   * @returns valor da discreção
   */
  getDiscreteValue(station: Feature<GeoJSON.Point>,timestamp: string):string{

    let color = timestamp+"color";
    switch (station.properties[color]) {
      case '#339944':
        return "fraca"
      case  '#FFCC00':
        return "moderada"
      case  '#FF8822':
        return "forte"
      default:
        return "muitoforte"
    }
  }


  private createBackdrop() {
    return this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'dark-backdrop',
      positionStrategy: this.overlay.position()
        .global()
        .centerHorizontally()
        .centerVertically()
    })
  }
  /**
     * Exibe o backdrop com um spinner centraliado
     */
   private showBackdrop() {
    this.backdrop.attach(new ComponentPortal(MatSpinner));
  }

  /**
   * Esconde o backdrop com o spinner
   */
  private hideBackdrop() {
    this.backdrop.detach();
  }

  /**
   * Converte uma duração de segundos em um formato de texto.
   * @param seconds duração a ser convertida.
   * @returns duração em dias, horas, minutos.
   */
  getFormattedDuration(seconds:number){
    return this.timespanService.secondsToFormattedText(seconds)
  }

  /**
   * Verifica se a estação está selecionada (botão da lupa clicado).
   * @param station 
   * @returns 
   */
  isStationSelected(station: Feature<GeoJSON.Point>) {
    const stationName = station.properties.nome_estacao_original;
    const index = this.selectedStations.indexOf(stationName);
    return index > -1;
  }

  /**
   * Quando uma estação for selecionada (botão da lupa), emite evento com o nome da estação, indicando
   *    se a estação foi adicionada ou removida do array dos nomes das estações.
   * @param station estação selecionada
   */
  onStationSelected(station: Feature<GeoJSON.Point>) {

    const stationName = station.properties.nome_estacao_original;
    this.toggleSelectedStation(stationName);
    this.stationToggled.emit(stationName);
  }

  /**
   * Alterna entre adicionar / remover o nome da estação do array de estações selecionadas.
   * @param stationName 
   */
  toggleSelectedStation(stationName: string) {
    const index = this.selectedStations.indexOf(stationName);
    if (index == -1) {
      this.selectedStations.push(stationName);
    }
    else {
      this.selectedStations.splice(index, 1);
    }
  }

  /**
   * Obtém a cor que a estação deveria assumir agora
   * 
   * @param value Feature equivalente a estação
   * @returns A cor que a estação deveria assumir agora
   */
   getStationColor(value : Feature<Point>){
    if (value.properties.region) return "#85a897"
    else if (value.properties.neighborhood && value.properties.nome_estacao_original != this.selectedNeighborhood) return "#ae86c4"
    else if (value.properties.neighborhood && value.properties.nome_estacao_original == this.selectedNeighborhood) return "#6d7274"
    else if(value.properties.id === this.selectedId) return "#6d7274"
    else return "#bccfd4"
  }

  /**
   * versão local do máximo para impedir que alguns valores da heatmatrix virem negativos
   * 
   * @param number 
   * @returns valor maximo entre 0 e o númeor
   */
  MaxZero(number:number){
    return Math.max(number,0)
  }

  addRegionToTable(region:Feature<Point>[]){
    this.activeRegions.push(region)
    if(this.displayState == 1) this.displayedArray = [region[0]].concat(this.displayedArray)
    if(this.displayState == 2)  this.displayedArray = [region[1]].concat(this.displayedArray)
    this.table.renderRows()
  }

  /**
   * Atualiza a região poligonal na tabela.
   * @param region região poligonal a ser atualizada.
   */
  updateRegionFromTable(region:Feature<Point>[]){
    let daIndex = this.displayedArray.findIndex( val =>{
      return val.id == region[0].id
    })
    if(this.displayState == 1) this.displayedArray[daIndex] = region[0]
    if(this.displayState == 2) this.displayedArray[daIndex] = region[1]

    let arIndex = this.activeRegions.findIndex( val =>{
      return val[0].id == region[0].id
    })
    this.activeRegions[arIndex] = region
    this.table.renderRows()
  }

  /**
   * Remove a entrada da região poligonal da tabela.
   * @param region região a ser removida.
   */
  removeRegionFromTable(region:Feature<Point>[]){
    let daIndex = this.displayedArray.findIndex( val =>{
      return val.id == region[0].id
    })
    this.displayedArray.splice(daIndex,1)
    let arIndex = this.activeRegions.findIndex( val =>{
      return val[0].id == region[0].id
    })
    this.activeRegions.splice(arIndex,1)
    this.table.renderRows()
  }

  /**
   * Alterna a exibição das linhas para estações.
   */
  displayStations(){
    this.displayState = 1
    this.displayedArray = this.activeRegions.map( val => {return  val[0]}).concat(this.stationArray)
    this.displayName = "Estações"
    this.changeTableDisplay.emit(this.activeRegions)
    this.table.renderRows()
  }

  /**
   * Alterna a exibição as linhas para bairros.
   */
  displayNeighborhoods(){
    this.displayState = 2
    this.displayedArray = this.activeRegions.map( val => {return  val[1]}).concat(this.neighborhoodArray);
    this.displayName = "Bairros"
    this.changeTableDisplay.emit(this.activeRegions)
    this.table.renderRows()
  }

  /**
   * Gera o csv da heatmatrix com os dados exibidos (estações ou bairros).
   */
  generateCSV(){
    if (this.displayedArray.length > 0){
      let filename = `heatmatrix-${TimeFormatter.toLocaleString(this.tempo[0])}_a_${TimeFormatter.toLocaleString(this.tempo[1])}.csv`
      CSVAux.createCSVtableFromProperties(this.timeColumns,this.displayedArray,this.displayName,filename)
    }
  }

  /**
   * 
   * @param disabled 
   */
  emitDragDisabled(disabled: boolean) {
    this.dragDisabled.emit(disabled);
  }

  /**
   * Obtém o valor a ser exibido em uma célula da heatmatrix intervalar.
   * @param element objeto com valor a ser exibido na coluna.
   * @param col descrição da coluna, com nome, texto do cabeçalho, cor, e flag de selecionado.
   */
  getCellValue(element: {[key: string]: any}, 
    col: {name: string, text: string, color: string, selected: boolean}) {

    if (this.durationColumnNames.includes(col.name)) {
      return this.getFormattedDuration(
        this.MaxZero(element.properties[col.name])
      );
    }

    let suffix = (col.name == 'percent') ? '%' : '';

    return this.round(element.properties[col.name], 2) + suffix;
  }

  /**
   * Recebe um array de features dos centroides dos bairros.
   * Alguns bairros são formados por mais de um polígono, p.ex. ILHA DA CONCEIÇÃO e ITAIPU.
   * Este método mantém, no caso de mais de um polígono, somente o centroide do 
   * polígono de maior área para representar o bairro.
   * @param features 
   */
  removeDuplicateNeighborhoodNames(features: Feature<Point>[]) {

    const names = new Set(features.map(f => f.properties.nome_estacao_original));
    const result: Feature<Point>[] = [];

    // percorrer os nomes de estações / bairros.
    for (const name of names) {

      const filtered = features.filter(f => name === f.properties.nome_estacao_original);

      let maxArea = Number.NEGATIVE_INFINITY;
      let maxFeature: Feature<Point> = null;

      // detectar a feature de maior área do bairro
      for (const f of filtered) {

        const { area } = f.properties;

        if (area < maxArea) {
          continue;
        }
        maxArea = area;
        maxFeature = f;
      }

      result.push(maxFeature);
    }

    return result;
  }
  
}
